// Story 21.1 — the Screen 24 TERM-BOUNDARY write (AC30; FR87).
//
// Action-in-path (Rule 06): `POST /admin/orgs/{id}/renew-contract`. This is a
// THIRD action beside `extend-contract` and `adjust-seats`, not a replacement
// for either: those two are mid-term CORRECTIONS that each move one column,
// while this one closes a term and opens the next, writing four columns in one
// statement (D4). A renewal is the only path that lowers `seat_consumed`.
//
// FOUR KEYS, the same four `useExtendContract` invalidates — and here every one
// of them is load-bearing at the same instant, which is not true of the sibling
// write. A renewal moves the end date (detail + directory), the seat figures
// (detail + directory), the 90-day renewal band on the console, and Screen 23's
// separate totals read. Dropping any one leaves a number on screen that the
// operator has just changed.
//
// NOTHING IS INVALIDATED ON A REFUSAL. `onSuccess` is the only hook here, so a
// 409 ORG-022 leaves every cache entry exactly as it was — the server wrote
// nothing, and a refetch would only re-render the same figures while implying
// something moved.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ADMIN_CONSOLE_QUERY_KEY } from "@/hooks/queries/useAdminConsole";
import { ADMIN_ORG_TOTALS_QUERY_KEY } from "@/hooks/queries/useAdminOrgTotals";
import { ADMIN_ORGS_QUERY_KEY } from "@/hooks/queries/useAdminOrgs";
import { adminOrgDetailQueryKey } from "@/hooks/queries/useAdminOrgDetail";
import { apiFetch } from "@/lib/api-client";

/** Mirrors `renew_contract/schemas.py :: RenewContractRequest` on the wire. */
export interface RenewContractRequest {
  /** `YYYY-MM-DD` — strictly after the stored end date (ORG-001). */
  contract_end_date: string;
  /** The new term's capacity. May be below the current one; never below `active_seat_count` (ORG-022). */
  seat_capacity: number;
}

/** Mirrors `renew_contract/schemas.py :: RenewContractData`. */
export interface RenewContractData {
  organization_id: string;
  /** The RELATIONSHIP start, unchanged by every renewal. */
  contract_start_date: string | null;
  contract_end_date: string;
  /**
   * The new term's first day. TODAY when the renewal was recorded on or before
   * the closing term's end date — the renewal is the boundary, and the seat
   * pool is re-established in the same write (Story 21.5, FR87) — and the day
   * after that end date when the contract had already lapsed. Never a future
   * date.
   */
  current_term_started_at: string;
  seat_capacity: number;
  /** Consumption after the reset: the carried active headcount. */
  seat_consumed: number;
  remaining: number;
}

export function renewContractPath(organizationId: string): string {
  return `/admin/orgs/${encodeURIComponent(organizationId)}/renew-contract`;
}

export function useRenewContract(organizationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RenewContractRequest) =>
      apiFetch<RenewContractData>(renewContractPath(organizationId), {
        method: "POST",
        body,
      }),
    // The default `networkMode: "online"` PAUSES a mutation while `navigator.onLine` is
    // false: `mutation.execute` dispatches `{ type: "pending", isPaused: true }` and then
    // stops, so `isPending` stays true INDEFINITELY and nothing is ever sent. The renewal
    // dialog gates every exit on `isPending` — Escape, outside click and Cancel all funnel
    // through its `closeDialog` guard, and there is no X — so under the default an offline
    // submit locked the operator in the dialog with no way out but a reload, losing the
    // typed date and seat count, with no refusal to render because no request was made.
    //
    // Second order, and worse: `QueryClient` subscribes `onlineManager` and calls
    // `resumePausedMutations()` on reconnect. An operator who escapes by navigating away
    // inside the SPA fires this irreversible POST later with `hasListeners()` false — and
    // `MutationObserver.#notify` gates the mutate-level `onSuccess`/`onError` on exactly
    // that — so the write lands and its 409 ORG-022 refusal renders nowhere at all.
    //
    // `"always"` runs `mutationFn` regardless of connectivity: offline it settles FAST as
    // an `ApiError(..., "NETWORK_ERROR", 0)`, `isPending` clears, the dialog unlocks and
    // the control's existing `onError` path shows the failure. `useGetEvaluation` and
    // `useListClients` carry the same fix for the read half of this hazard.
    networkMode: "always",
    // PINNED, not inherited. Mutations already default to zero retries (`mutation.ts`:
    // `retry: this.options.retry ?? 0`) — unlike queries, which default to 3, which is why
    // both read precedents above had to say `false`. But that default is overridable from
    // `QueryClient.defaultOptions.mutations`, and a retry here is a SECOND POST of a term
    // boundary that no endpoint can put back. `false` fences this write against an
    // app-wide default it must never pick up, and keeps the offline settle to one pass.
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminOrgDetailQueryKey(organizationId) });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORGS_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONSOLE_QUERY_KEY] });
      void queryClient.invalidateQueries({ queryKey: [ADMIN_ORG_TOTALS_QUERY_KEY] });
    },
  });
}
